import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ReleaseToDeconStep } from '../types/release-to-decon';
import { useReleaseToDeconData, useReleaseToDeconDiscovery } from '../hooks';
import {
  clearStoredLocation,
  readStoredLocation,
  writeStoredLocation,
} from '../utils/releaseToDeconStorage';
import { DiscoveryCard } from './DiscoveryCard';
import { ErrorCard } from './ErrorCard';
import { HubLoadingCard } from './HubLoadingCard';
import { HubStatusCard } from './HubStatusCard';
import { LoadingCard } from './LoadingCard';
import { LocationListCard } from './LocationListCard';
import {
  contentWrapVariantSx,
  discoveryContentWrapSx,
  featurePageTitleCompactWrapSx,
  featurePageStandaloneTitleWrapSx,
  featurePageTitleTextSx,
  featurePageTitleWrapSx,
  pageContainerVariantSx,
  popupOverlaySx,
  selectedFlowLocationSx,
  selectedFlowTitleSx,
} from './styles';
import { useContentTitleVisibility } from '../../../shared';
import { LocationEntity, useAndroid } from '@peritasai/repository';

export const ReleaseToDeconPage = () => {
  const contentTitleVisibility = useContentTitleVisibility();
  const theme = useTheme();
  const isDesktopSelectedFlow = useMediaQuery(theme.breakpoints.up('md'));
  const initialStoredLocationRef = useRef<LocationEntity | null>(
    readStoredLocation()
  );
  const {
    step,
    setStep,
    locations,
    visibleLocations,
    showAllLocations,
    setShowAllLocations,
    loadLocations,
  } = useReleaseToDeconData();
  const [selectedLocation, setSelectedLocation] =
    useState<LocationEntity | null>(initialStoredLocationRef.current);
  const [isBootstrappingStoredLocation, setIsBootstrappingStoredLocation] =
    useState(() => initialStoredLocationRef.current !== null);
  const { detectedTrays, clearDetectedTrays, handleCancelDiscovery } =
    useReleaseToDeconDiscovery({
      step,
      setStep,
      selectedLocation,
    });
  const { isAndroid, locationId } = useAndroid();
  const hasStoredLocationBootstrap = !!initialStoredLocationRef.current;
  const shouldHideBootstrapIntermediates =
    hasStoredLocationBootstrap &&
    isBootstrappingStoredLocation &&
    (step === ReleaseToDeconStep.LOADING_LOCATIONS ||
      step === ReleaseToDeconStep.SELECT_LOCATION);

  const isDiscoveryStep =
    step === ReleaseToDeconStep.LOADING_HUB || shouldHideBootstrapIntermediates;
  const hasMultipleHubs = locations.length > 1 && !isAndroid;
  const shouldRenderStandaloneReleaseTitle =
    !shouldHideBootstrapIntermediates &&
    !isBootstrappingStoredLocation &&
    (step === ReleaseToDeconStep.LOADING_LOCATIONS ||
      step === ReleaseToDeconStep.ERROR_LOCATIONS ||
      step === ReleaseToDeconStep.SELECT_LOCATION ||
      step === ReleaseToDeconStep.HUB_OFFLINE ||
      step === ReleaseToDeconStep.HUB_IN_USE);
  const shouldRenderMobileReleaseRouteTitle =
    !shouldHideBootstrapIntermediates &&
    !isBootstrappingStoredLocation &&
    !isDesktopSelectedFlow &&
    false;
  const standaloneReleaseLocationName =
    step === ReleaseToDeconStep.HUB_OFFLINE ||
    step === ReleaseToDeconStep.HUB_IN_USE
      ? selectedLocation?.name ?? null
      : null;

  useLayoutEffect(() => {
    contentTitleVisibility?.setHideContentTitle(true);
    contentTitleVisibility?.setHideContentTitleOnDesktop(false);
  }, [contentTitleVisibility]);

  const renderReleaseFeatureTitle = ({
    locationName,
    showDesktopTitle,
  }: {
    locationName?: string | null;
    showDesktopTitle: boolean;
  }) => {
    const titleSx = locationName ? selectedFlowTitleSx : featurePageTitleTextSx;
    const titleWrapSx = locationName
      ? featurePageTitleCompactWrapSx
      : showDesktopTitle
      ? featurePageStandaloneTitleWrapSx
      : featurePageTitleWrapSx;
    return (
      <Box sx={titleWrapSx}>
        <Typography
          component="h1"
          sx={{
            ...titleSx,
            display: { xs: 'block', md: 'none' },
          }}
        >
          Release to Decon
        </Typography>
        {showDesktopTitle && (
          <Typography
            component="p"
            sx={{
              ...titleSx,
              display: { xs: 'none', md: 'block' },
            }}
          >
            Release to Decon
          </Typography>
        )}
        {locationName && (
          <Typography sx={selectedFlowLocationSx}>{locationName}</Typography>
        )}
      </Box>
    );
  };

  const handleSelectLocation = useCallback(
    (location: LocationEntity) => {
      setSelectedLocation(location);
      writeStoredLocation(location);
      clearDetectedTrays();
      setIsBootstrappingStoredLocation(false);
      setStep(ReleaseToDeconStep.LOADING_HUB);
    },
    [clearDetectedTrays, setStep]
  );

  const handleChangeLocation = useCallback(() => {
    handleCancelDiscovery();
    clearStoredLocation();
    initialStoredLocationRef.current = null;
    setSelectedLocation(null);
    clearDetectedTrays();
    setIsBootstrappingStoredLocation(false);
    setStep(ReleaseToDeconStep.SELECT_LOCATION);
  }, [handleCancelDiscovery, clearDetectedTrays, setStep]);

  useEffect(() => {
    const storedLocation = initialStoredLocationRef.current;
    const storedLocationId = storedLocation?.id ?? locationId;
    if (!storedLocationId) return;
    if (step !== ReleaseToDeconStep.SELECT_LOCATION) return;

    const matchedLocation = locations.find(
      (location) => location.id === storedLocationId
    );
    if (!matchedLocation) {
      clearStoredLocation();
      initialStoredLocationRef.current = null;
      setIsBootstrappingStoredLocation(false);
      return;
    }

    setSelectedLocation(matchedLocation);
    clearDetectedTrays();
    setStep(ReleaseToDeconStep.LOADING_HUB);
  }, [
    clearDetectedTrays,
    isBootstrappingStoredLocation,
    locationId,
    locations,
    setStep,
    step,
  ]);

  useEffect(() => {
    if (!hasStoredLocationBootstrap || !isBootstrappingStoredLocation) return;
    if (
      step === ReleaseToDeconStep.HUB_OFFLINE ||
      step === ReleaseToDeconStep.HUB_IN_USE ||
      step === ReleaseToDeconStep.ERROR_LOCATIONS
    ) {
      setIsBootstrappingStoredLocation(false);
    }
  }, [hasStoredLocationBootstrap, isBootstrappingStoredLocation, step]);

  return (
    <Box sx={pageContainerVariantSx(isDiscoveryStep)}>
      <Box sx={contentWrapVariantSx(isDiscoveryStep)}>
        {shouldRenderStandaloneReleaseTitle &&
          renderReleaseFeatureTitle({
            locationName: standaloneReleaseLocationName,
            showDesktopTitle: true,
          })}

        {shouldRenderMobileReleaseRouteTitle &&
          renderReleaseFeatureTitle({
            showDesktopTitle: false,
          })}

        {shouldHideBootstrapIntermediates && selectedLocation && (
          <Box sx={discoveryContentWrapSx}>
            <HubLoadingCard />
          </Box>
        )}

        {!shouldHideBootstrapIntermediates &&
          !isBootstrappingStoredLocation &&
          step === ReleaseToDeconStep.LOADING_LOCATIONS && (
            <LoadingCard rows={5} cardWidth="100%" cardMaxWidth="100%" />
          )}

        {!isBootstrappingStoredLocation &&
          step === ReleaseToDeconStep.ERROR_LOCATIONS && (
            <Box sx={popupOverlaySx}>
              <ErrorCard
                title="Something went wrong"
                description="The release station locations cannot be loaded."
                onCancel={() => setStep(ReleaseToDeconStep.SELECT_LOCATION)}
                onRetry={() => void loadLocations()}
              />
            </Box>
          )}

        {!shouldHideBootstrapIntermediates &&
          !isBootstrappingStoredLocation &&
          step === ReleaseToDeconStep.SELECT_LOCATION && (
            <LocationListCard
              locations={visibleLocations}
              showAll={showAllLocations}
              showViewAll={locations.length > 4}
              onToggleViewAll={() => setShowAllLocations((prev) => !prev)}
              onSelect={handleSelectLocation}
            />
          )}

        {!isBootstrappingStoredLocation &&
          step === ReleaseToDeconStep.HUB_OFFLINE && (
            <Box sx={popupOverlaySx}>
              <Box sx={{ width: '100%', maxWidth: '596px' }}>
                <HubStatusCard
                  title={`${
                    selectedLocation?.name ?? ''
                  } Release Station Offline`}
                  message="You cannot release trays right now because the Release Station is disconnected."
                  secondMessage="Check the station is powered on and connected, then re-scan trays."
                  onConfirm={() => {
                    setStep(ReleaseToDeconStep.SELECT_LOCATION);
                  }}
                />
              </Box>
            </Box>
          )}

        {!isBootstrappingStoredLocation &&
          step === ReleaseToDeconStep.HUB_IN_USE && (
            <Box sx={popupOverlaySx}>
              <Box sx={{ width: '100%', maxWidth: '596px' }}>
                <HubStatusCard
                  title={`${
                    selectedLocation?.name ?? ''
                  } Release Station In Use`}
                  message="You cannot start a session because the Release Station is currently in use by another user."
                  secondMessage="Check the selected location, and re-scan trays when it's free."
                  onConfirm={() => {
                    setStep(ReleaseToDeconStep.SELECT_LOCATION);
                  }}
                />
              </Box>
            </Box>
          )}

        {step === ReleaseToDeconStep.LOADING_HUB &&
          !shouldHideBootstrapIntermediates &&
          selectedLocation && (
            <Box sx={discoveryContentWrapSx}>
              <DiscoveryCard
                step={step}
                selectedLocation={selectedLocation}
                detectedTrays={detectedTrays}
                showFlowTitle={isDesktopSelectedFlow}
                showCancel={hasMultipleHubs}
                onCancel={handleChangeLocation}
              />
            </Box>
          )}
      </Box>
    </Box>
  );
};
